import { useState } from 'react'
import { useRevenueAnalytics } from '@/hooks/useAnalytics'
import {
  useTransactions,
  useTransactionSummary,
  useRefundTransaction,
} from '@/hooks/useTransactions'
import { useHasRole } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DataTable } from '@/components/common/DataTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { FilterDropdown } from '@/components/common/FilterDropdown'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { QueryError } from '@/components/QueryError'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Transaction, TransactionsFilter, TransactionType, TransactionStatus } from '@/types'
import { formatCurrency, formatDate, formatNumber } from '@/lib/utils'
import { DollarSign, CreditCard, Package, RotateCcw } from 'lucide-react'

type Period = '7d' | '30d' | '90d' | '6m' | '1y'

const typeOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'subscription', label: 'Suscripcion' },
  { value: 'package', label: 'Paquete' },
  { value: 'refund', label: 'Reembolso' },
]

const statusOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'completed', label: 'Completado' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'failed', label: 'Fallido' },
  { value: 'refunded', label: 'Reembolsado' },
]

export const RevenuePage = () => {
  const [period, setPeriod] = useState<Period>('30d')
  const [txFilters, setTxFilters] = useState<TransactionsFilter>({ page: 1, limit: 20 })
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [refundTxId, setRefundTxId] = useState<string | null>(null)
  const [refundReason, setRefundReason] = useState('')

  const canRefund = useHasRole(['super_admin', 'admin', 'finance'])

  const {
    data: revenue,
    isLoading: revenueLoading,
    isError: revenueError,
    isFetching: revenueFetching,
    refetch: refetchRevenue,
  } = useRevenueAnalytics(period)
  const { data: summary } = useTransactionSummary()

  const currentTxFilters: TransactionsFilter = {
    ...txFilters,
    type: typeFilter !== 'all' ? (typeFilter as TransactionType) : undefined,
    status: statusFilter !== 'all' ? (statusFilter as TransactionStatus) : undefined,
  }
  const {
    data: txData,
    isLoading: txLoading,
    isError: txError,
    isFetching: txFetching,
    refetch: refetchTransactions,
  } = useTransactions(currentTxFilters)
  const refundTransaction = useRefundTransaction()

  const handleRefund = async () => {
    if (!refundTxId || !refundReason) return
    await refundTransaction.mutateAsync({ id: refundTxId, request: { reason: refundReason } })
    setRefundTxId(null)
    setRefundReason('')
  }

  const txColumns = [
    {
      key: 'user',
      header: 'Usuario',
      cell: (tx: Transaction) => (
        <div>
          <p className="text-sm font-medium">{tx.user_name || '-'}</p>
          <p className="text-xs text-muted-foreground">{tx.user_email}</p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Tipo',
      cell: (tx: Transaction) => <span className="text-sm capitalize">{tx.type}</span>,
    },
    {
      key: 'amount',
      header: 'Monto',
      cell: (tx: Transaction) => (
        <span className="font-medium">{formatCurrency(tx.total_amount)}</span>
      ),
    },
    {
      key: 'payment',
      header: 'Metodo',
      cell: (tx: Transaction) => (
        <span className="text-sm capitalize text-muted-foreground">{tx.payment_method}</span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (tx: Transaction) => <StatusBadge status={tx.status} />,
    },
    {
      key: 'date',
      header: 'Fecha',
      cell: (tx: Transaction) => (
        <span className="text-sm text-muted-foreground">{formatDate(tx.created_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (tx: Transaction) =>
        canRefund && tx.status === 'completed' ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Reembolsar transacción"
            title="Reembolsar"
            onClick={() => setRefundTxId(tx.id)}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        ) : null,
    },
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Ingresos</h1>

      {(revenueError || txError) && (
        <QueryError
          message="No se pudieron cargar los datos de ingresos o transacciones."
          onRetry={() => {
            refetchRevenue()
            refetchTransactions()
          }}
          isRetrying={revenueFetching || txFetching}
        />
      )}

      {/* Period Tabs + Summary Cards */}
      <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
        <TabsList>
          <TabsTrigger value="7d">7 dias</TabsTrigger>
          <TabsTrigger value="30d">30 dias</TabsTrigger>
          <TabsTrigger value="90d">90 dias</TabsTrigger>
          <TabsTrigger value="6m">6 meses</TabsTrigger>
          <TabsTrigger value="1y">1 anio</TabsTrigger>
        </TabsList>
      </Tabs>

      {revenueLoading ? (
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : revenue ? (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ingresos totales</p>
                  <p className="text-2xl font-bold">{formatCurrency(revenue.total)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Suscripciones</p>
                  <p className="text-2xl font-bold">{formatCurrency(revenue.subscriptions)}</p>
                </div>
                <CreditCard className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Paquetes</p>
                  <p className="text-2xl font-bold">{formatCurrency(revenue.packages)}</p>
                </div>
                <Package className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Revenue Chart */}
      {revenue?.data?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>Ingresos en el tiempo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenue.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => formatDate(v, { month: 'short', day: 'numeric' })}
                />
                <YAxis tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  formatter={(v: number) => formatCurrency(v)}
                  labelFormatter={(l) => formatDate(l)}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="subscriptions"
                  stroke="#7BA68A"
                  name="Suscripciones"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="packages"
                  stroke="#D4942A"
                  name="Paquetes"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#5B8FA8"
                  name="Total"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ) : null}

      {/* Transactions */}
      <div>
        <h2 className="mb-4 text-xl font-bold">Transacciones</h2>
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <FilterDropdown
            label="Tipo"
            options={typeOptions}
            value={typeFilter}
            onChange={(v) => {
              setTypeFilter(v)
              setTxFilters((f) => ({ ...f, page: 1 }))
            }}
          />
          <FilterDropdown
            label="Estado"
            options={statusOptions}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v)
              setTxFilters((f) => ({ ...f, page: 1 }))
            }}
          />
          {summary && (
            <span className="ml-auto text-sm text-muted-foreground">
              Total: {formatCurrency(summary.total_revenue)} (
              {formatNumber(summary.total_transactions)} transacciones)
            </span>
          )}
        </div>

        <DataTable
          columns={txColumns}
          data={txData?.transactions ?? []}
          isLoading={txLoading}
          keyExtractor={(tx) => tx.id}
          pagination={
            txData
              ? {
                  page: txData.page,
                  pages: txData.pages,
                  onPageChange: (page) => setTxFilters((f) => ({ ...f, page })),
                }
              : undefined
          }
          emptyMessage="No hay transacciones"
        />
      </div>

      {/* Refund Dialog */}
      <Dialog open={!!refundTxId} onOpenChange={(open) => !open && setRefundTxId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Procesar reembolso</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Razon del reembolso</Label>
              <Input
                placeholder="Razon..."
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTxId(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleRefund}
              disabled={refundTransaction.isPending || !refundReason}
            >
              {refundTransaction.isPending ? 'Procesando...' : 'Reembolsar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
